/**
 * Daily values table (DATA_VISUALIZATION_FRONTEND_PLAN §2.1). One row per day,
 * one column per selected series, a TOTAL row taken from each series'
 * server-authoritative total (never re-summed client-side). CSV export writes
 * the RAW decimal strings byte-for-byte — no float round-trip.
 */
import { Button, Group, Paper, ScrollArea, Table, Text } from "@mantine/core";
import { IconClock, IconDownload, IconPencil } from "@tabler/icons-react";
import { enumerateDays } from "../../lib/dateRange";
import { downloadBlob } from "../../lib/download";
import {
  type CorrectionDisplayMode,
  type SeriesOverlay,
  resolveCellDisplay,
} from "./correctionOverlay";
import {
  type ChartSeriesInput,
  buildCsv,
  formatBucketLabel,
  formatCell,
  unionDays,
} from "./series-data";
import { unitLabel } from "./units";

type Props = {
  series: ChartSeriesInput[];
  /** Deterministic export filename (no timestamps — locked decision #8). */
  filename?: string;
  /**
   * Requested gas-day window. When given, the table + CSV span the whole range
   * so missing boundary days show as blank rows (matching the chart). Omit when
   * there is no requested range (e.g. the upload-rows drill-in).
   */
  range?: { start: string; end: string };
  /** "daily" (gas-day rows) or "hourly" (one row per bucket; coverage shown). */
  granularity?: "daily" | "hourly";
  /**
   * Correction overlay (display-only; nothing is recomputed). Keyed by
   * `ChartSeriesInput.id`. Omitted → the table renders raw DB values exactly
   * as before (the upload-rows drill-in never passes this).
   */
  corrections?: { mode: CorrectionDisplayMode; overlays: Map<string, SeriesOverlay> };
};

export function SeriesTable({
  series,
  filename = "data-explorer-series.csv",
  range,
  granularity = "daily",
  corrections,
}: Props) {
  const withData = series.filter((s) => s.points.length > 0);
  const hourly = granularity === "hourly";
  const corrMode: CorrectionDisplayMode = corrections?.mode ?? "db";
  const overlayFor = (i: number) =>
    corrMode !== "db" && withData[i] ? corrections?.overlays.get(withData[i].id) : undefined;
  // Hourly buckets aren't gas-days, so span the actual bucket keys; daily spans
  // the requested gas-day range so missing boundary days show as blank rows.
  const baseDays = range && !hourly ? enumerateDays(range.start, range.end) : unionDays(withData);
  // Union in overlay cell keys so an approved substitution on a bucket with no
  // DB point still gets a row (matters for hourly; daily is range-enumerated).
  const days = (() => {
    if (corrMode === "db") return baseDays;
    const set = new Set(baseDays);
    let extended = false;
    withData.forEach((_, i) => {
      for (const [key, cell] of overlayFor(i)?.cells.entries() ?? []) {
        // A pending-only cell earns a synthetic row only in "all" mode (it
        // renders yellow with no value); approved cells always get their row.
        if (!set.has(key) && (cell.approved != null || corrMode === "all")) {
          set.add(key);
          extended = true;
        }
      }
    });
    return extended ? [...set].sort() : baseDays;
  })();
  // Per-column flag: does any approved correction overlay this series? Drives
  // the TOTAL † (the server total is pre-correction) and the CSV relabel.
  const colHasApproved = withData.map((_, i) => {
    const overlay = overlayFor(i);
    if (!overlay) return false;
    for (const cell of overlay.cells.values()) if (cell.approved != null) return true;
    return false;
  });
  const anyApproved = colHasApproved.some(Boolean);
  const anyOverlay = corrMode !== "db" && withData.some((_, i) => overlayFor(i) != null);

  if (withData.length === 0) {
    return (
      <Paper p="lg" withBorder>
        <Text c="dimmed" size="sm">
          No series to tabulate yet.
        </Text>
      </Paper>
    );
  }

  // Index each series' values (and sample counts) by bucket key for O(1) lookups.
  const valueByDay = withData.map((s) => {
    const m = new Map<string, string | null>();
    for (const p of s.points) m.set(p.day, p.value);
    return m;
  });
  const coverageByDay = withData.map((s) => {
    const m = new Map<string, number | null>();
    for (const p of s.points) m.set(p.day, p.n_readings ?? null);
    return m;
  });
  // A bucket is "thin" when its sample count is well below the series' best — a
  // relative completeness flag (no per-column hard-coded expected sample rate).
  const peakN = withData.map((s) => s.points.reduce((mx, p) => Math.max(mx, p.n_readings ?? 0), 0));

  const onExport = () => {
    const csv = buildCsv(withData, days, corrections);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    downloadBlob(blob, filename);
  };

  return (
    <Paper withBorder p="md">
      <Group justify="space-between" mb="sm">
        <Text size="sm" fw={600}>
          Daily values
        </Text>
        <Button
          size="compact-sm"
          variant="default"
          leftSection={<IconDownload size={14} />}
          onClick={onExport}
        >
          Export CSV
        </Button>
      </Group>
      <ScrollArea.Autosize mah={460} type="auto">
        <Table fz="sm" stickyHeader highlightOnHover withTableBorder withColumnBorders>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{hourly ? "Hour (PT)" : "Day (PT)"}</Table.Th>
              {withData.map((s) => (
                <Table.Th key={s.id} ta="right">
                  <Text size="xs" fw={600}>
                    {s.label}
                  </Text>
                  <Text size="xs" c="dimmed" fw={400}>
                    {unitLabel(s.unit)}
                  </Text>
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {days.map((day) => (
              <Table.Tr key={day}>
                <Table.Td ff="monospace">{formatBucketLabel(day, granularity)}</Table.Td>
                {valueByDay.map((m, i) => {
                  const n = coverageByDay[i]?.get(day) ?? null;
                  const peak = peakN[i] ?? 0;
                  const thin = n != null && peak > 0 && n < 0.9 * peak;
                  const cell = overlayFor(i)?.cells.get(day);
                  const display = resolveCellDisplay(m.get(day) ?? null, cell, corrMode);
                  const samples = n != null ? `${n} samples` : null;
                  // Correction styling outranks the thin-coverage amber; the
                  // tooltip merges both facts so neither signal is lost.
                  const title = [samples, display.tooltip].filter(Boolean).join(" · ") || undefined;
                  const marker = display.marker;
                  return (
                    <Table.Td
                      key={withData[i]?.id ?? i}
                      ta="right"
                      ff="monospace"
                      c={
                        marker === "approved"
                          ? "red.9"
                          : marker === "pending"
                            ? "yellow.9"
                            : thin
                              ? "orange"
                              : undefined
                      }
                      bg={
                        marker === "approved"
                          ? "red.1"
                          : marker === "pending"
                            ? "yellow.1"
                            : undefined
                      }
                      td={display.excluded ? "line-through" : undefined}
                      title={title}
                      data-corrected={marker ?? undefined}
                    >
                      {marker === "approved" ? (
                        <IconPencil size={11} style={{ marginRight: 4, verticalAlign: -1 }} />
                      ) : marker === "pending" ? (
                        <IconClock size={11} style={{ marginRight: 4, verticalAlign: -1 }} />
                      ) : null}
                      {formatCell(display.value, 2)}
                    </Table.Td>
                  );
                })}
              </Table.Tr>
            ))}
          </Table.Tbody>
          <Table.Tfoot>
            <Table.Tr>
              <Table.Th>Total</Table.Th>
              {withData.map((s, i) => (
                <Table.Th key={s.id} ta="right" ff="monospace">
                  {s.total ? formatCell(s.total.value, 2) : "—"}
                  {colHasApproved[i] ? (
                    <span title="Server period total — excludes correction overlays">†</span>
                  ) : null}
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Tfoot>
        </Table>
      </ScrollArea.Autosize>
      <Text size="xs" c="dimmed" mt="xs">
        Totals are the server's period figures — the contract value, not a client re-sum.
        {anyApproved
          ? " † columns have approved correction overlays; the total stays the server's pre-correction figure."
          : ""}
        {anyOverlay
          ? " Red = approved correction applied (the displayed value is the corrected value); yellow = proposed correction (not applied); strikethrough = excluded. Totals and CSV exports never include pending proposals."
          : ""}
        {hourly
          ? " Amber cells flag hours with notably fewer samples than the source's best (lower data completeness); hover for the count."
          : ""}
      </Text>
    </Paper>
  );
}
